import { Form, FormInstance, Input, Select } from 'antd';

import { useRef, useState } from 'react';
import { createGame, getGameKeyId } from '../../../api';
import { validateNameLength } from '../../../lib';
import {
  messageError,
  messageSuccess,
  messageWarning,
} from '../../../components/message';
import { Dispatch } from 'redux';
import { ModalWrap } from '../modal-wrap';
import { GameEnvRegion } from '../../../model/api-modules/env';
import { useSelector, useDispatch } from 'react-redux';
import { IRootState } from '../../../store/reducers';
import { getGameEnvTypeOptions } from '../../game-content/lib/game-env';
import {
  getEnvCollectionList,
  GamesAction,
  getGameList,
} from '../../../store/modules/games';
import './style.scss';
import getFormatMessage from '../../../locales/get-format-message';

interface IProps {
  readonly onReload: () => void;
  readonly onClosed: () => void;
}
export const CreateGameModal = (props: IProps) => {
  const formRef = useRef<FormInstance<any>>(null);
  const gamesDispatch = useDispatch<Dispatch<GamesAction>>();
  const selectStudio = useSelector(
    (state: IRootState) => state.studio.selectStudio
  );
  const games = useSelector((state: IRootState) => state.games.games);
  const [loading, setLoading] = useState<boolean>(false);

  const createColor = () => {
    const colors = ['#165DFF', '#722ED1', '#F7BA1E'];
    const randomIndex = Math.floor(Math.random() * colors.length);
    return colors[randomIndex];
  };

  const createGameMethods = async (
    Name: string,
    StudioID: string,
    GameID: string,
    GameKey: string,
    Region: GameEnvRegion,
    Code: string
  ) => {
    try {
      const res = await createGame({
        Name,
        studioID: StudioID,
        gameID: GameID,
        GameKey,
        EnvRegion: Region,
        ProjectCode: Code,
        Color: createColor(),
      });
      if (res === 0) {
        messageSuccess(getFormatMessage('create_title_success_desc'));
        await getEnvCollectionList(gamesDispatch)(StudioID);
        await getGameList(gamesDispatch)();
        props.onReload();
        props.onClosed();
      } else if (res === 101122) {
        messageError(getFormatMessage('game_code_exists'));
      } else {
        messageError(getFormatMessage('creation_fail_try'));
      }
    } catch (err: any) {
      messageError(err?.apiError?.message);
    }
  };

  const onOk = async () => {
    const tempGames = games.filter(
      (v) => v.gameID && v.studioID === selectStudio?.studioID
    );
    if (tempGames.length > 10) {
      messageWarning(getFormatMessage('exceed_limit_10'));
      return false;
    }
    if (!formRef.current?.validateFields()) {
      return false;
    }
    const value = await formRef.current.validateFields();
    const nameRule = /^[^()%/\\&><]+$/;
    if (!nameRule.test(value.TitleName.trim())) {
      messageWarning(getFormatMessage('name_verification_failed'));
      return false;
    }
    try {
      setLoading(true);
      const game = await getGameKeyId();
      if (game && Object.keys(game).length > 0 && selectStudio?.studioID) {
        await createGameMethods(
          value.TitleName.trim(),
          selectStudio.studioID,
          game.gameID,
          game.gameKey,
          value.EnvRegion,
          value.ProjectCode
        );
      }

      setLoading(false);
      return true;
    } catch (err: any) {
      messageError(err?.apiError?.message);
      setLoading(false);
      return false;
    }
  };

  const onClose = () => {
    props.onClosed();
  };
  const handleRegionChange = (_value: string) => {};

  const getPopupContainer = (): HTMLElement => {
    const modalBody = document.querySelector('.ant-modal-body');
    if (modalBody) {
      return modalBody as HTMLElement; // 显式转换为 HTMLElement
    }
    return document.body as HTMLElement; // 如果 modalBody 为 null，则返回 document.body
  };

  const renderInput = () => {
    return (
      <Form
        ref={formRef}
        className="c-main-form create-game-form"
        name="basic"
        initialValues={{ remember: true }}
        autoComplete="off"
        layout="vertical"
        colon={false}
        requiredMark={false}
      >
        <Form.Item
          label={getFormatMessage('new_title_form_label')}
          name="TitleName"
          rules={[
            {
              required: true,
              validator: validateNameLength(
                getFormatMessage('title_name_lowercase')
              ),
            },
          ]}
        >
          <Input
            className="c-antd-input-xl-main"
            maxLength={64}
            autoFocus
            placeholder={getFormatMessage('please_enter_title_name')}
            spellCheck="false"
          />
        </Form.Item>
        <Form.Item
          label={getFormatMessage('code_name_form_label')}
          name="ProjectCode"
          rules={[
            {
              required: true,
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject(getFormatMessage('code_is_required'));
                }
                if (!/^[a-zA-Z0-9]{1,9}$/.test(value)) {
                  return Promise.reject(
                    new Error(getFormatMessage('code_is_required'))
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            className="c-antd-input-xl-main"
            maxLength={64}
            placeholder={getFormatMessage('please_enter_code_name')}
            spellCheck="false"
          />
        </Form.Item>
        <span className="region-tips">
          {getFormatMessage('title_unique_code')}
        </span>
        <Form.Item
          label={getFormatMessage('region')}
          name="EnvRegion"
          colon={false}
          rules={[
            { required: true, message: getFormatMessage('region_is_required') },
          ]}
        >
          <Select
            className="c-antd-select-xl-main"
            onChange={handleRegionChange}
            placeholder={getFormatMessage('please_select_the_region')}
            options={getGameEnvTypeOptions()}
            getPopupContainer={getPopupContainer}
          />
        </Form.Item>
        <span className="region-tips">
          {getFormatMessage('region_default_env_desc')}
        </span>
      </Form>
    );
  };

  return (
    <ModalWrap
      loading={loading}
      onOk={onOk}
      oKText={getFormatMessage('create')}
      cancelText={getFormatMessage('cancel')}
      width="480px"
      onClose={onClose}
      title={getFormatMessage('new_title')}
      className="create-game-modal"
    >
      {renderInput()}
    </ModalWrap>
  );
};
